import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';
import { LOADING_DELAY_MS } from '@/constants/timings';
import { SkeletonLoader } from '@/components/ui/molecules/SkeletonLoader';
import { SFooterNav, NavItem } from '@/components/ui/organisms/SFooterNav';
import { SButton } from '@/components/ui/atoms/SButton';
import SDialog from '@/components/ui/molecules/SDialog';
import ShopDrawer from '@/components/ui/organisms/ShopDrawer';
import { useBookings } from '@/hooks/useBookings';
import { BookingDetailsDto } from '@/services/booking.service';
import { BannerCarousel } from '@/components/ui/organisms/BannerCarousel';
import { BookingCarousel } from '@/components/ui/organisms/BookingCarousel';
import { FacilityType, facilityTypeTitles } from '@/types/booking.types';
import { useBanners } from '@/hooks/useBanners';
import MeetYourPro from '@/components/ui/organisms/MeetYourPro';
import TeebxMethod from '@/components/ui/organisms/TeebxMethod';
import { CalendarBlank } from 'phosphor-react';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeNav, setActiveNav] = useState<NavItem>('home');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<string | null>(null);
  const [isShopDrawerOpen, setIsShopDrawerOpen] = useState(false);

  // Get user information using hooks
  const {
    bookings,
    isLoading: bookingsLoading,
    error: bookingsError,
    cancelBooking,
    cancellingBookingId,
    isCheckingIn,
    isRefreshing,
  } = useBookings();

  const { banners } = useBanners();

  // Show up to 4 active/upcoming bookings on home screen
  const filteredBookings = useMemo(() => {
    if (!bookings || bookings.length === 0) return [];
    
    const now = new Date();
    const allBookings = bookings as BookingDetailsDto[];
    
    // Filter only active/upcoming bookings (not cancelled and not past)
    // A session is only "past" once its end time has passed — an in-progress
    // session (start <= now < end) must remain visible so the user can check in.
    const activeUpcoming = allBookings.filter((booking: BookingDetailsDto) => {
      if (booking.status === 'cancelled') return false;

      const bookingEndDateTime = new Date(booking.endTime);
      return bookingEndDateTime >= now;
    });
    
    // Sort by start time (earliest first) and return up to 4 bookings
    return activeUpcoming
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      .slice(0, 5);
  }, [bookings]);

  // Create a memoized cancel handler that includes loading state
  const handleCancelBooking = (bookingId: string) => {
    if (!cancellingBookingId) {
      setBookingToCancel(bookingId);
      setCancelDialogOpen(true);
    }
  };

  const handleBookingReschedule = (bookingId: string) => {
    const booking = filteredBookings.find(b => (b._id || b.id) === bookingId);
    if (!booking) return;
    navigate('/booking-slots', {
      state: {
        bookingType: {
          facilityType: booking.facility as FacilityType,
          title: facilityTypeTitles[booking.facility as FacilityType] || booking.facility,
          price: 0,
        },
        selectedLocation: {
          ...(booking.location || {}),
          timezone: booking.timezone,
        },
        rescheduleBookingId: booking.id || booking._id,
        rescheduleRoomId: booking.assignedRooms?.[0]?.id,
      },
    });
  };

  // Handle confirm cancel from dialog
  const handleConfirmCancel = () => {
    if (bookingToCancel && !cancellingBookingId) {
      cancelBooking(bookingToCancel);
      setCancelDialogOpen(false);
      setBookingToCancel(null);
    }
  };

  // Handle cancel dialog close
  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setBookingToCancel(null);
  };

  // Handle shop drawer
  const handleVisitShop = () => {
    setIsShopDrawerOpen(true);
  };

  const handleShopDrawerClose = () => {
    setIsShopDrawerOpen(false);
  };

  const handleShopContinue = () => {
    navigate('/shop');
  };

  // Create a memoized check-in handler that includes loading state
  const handleCheckInBooking = (bookingId: string) => {
    if (!isCheckingIn) {
      // Find the booking to get location coordinates
      const booking = filteredBookings.find(b => (b._id || b.id) === bookingId);

      // TEMP: bypass location verification — go directly to open gate
      navigate('/access-control/open-gate', {
        state: {
          bookingId,
          facilityName: facilityTypeTitles[booking?.facility as FacilityType] ?? booking?.facility ?? undefined,
          facilityType: booking?.facility,
        },
      });
    }
  };

  // Handle Open Gate
  const handleOpenGate = (bookingId: string) => {
    const booking = filteredBookings.find(b => (b._id || b.id) === bookingId);

    // TEMP: bypass location verification — go directly to open gate
    navigate('/access-control/open-gate', {
      state: {
        bookingId,
        facilityName: facilityTypeTitles[booking?.facility as FacilityType] ?? booking?.facility ?? undefined,
        facilityType: booking?.facility,
      },
    });
  };

  // Fallback location from localStorage for immediate display
  // const fallbackLocation = getUserLocationFromStorage();

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, LOADING_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  const handleNavClick = (item: NavItem) => {
    setActiveNav(item);
    logger.debug('Navigation clicked:', item);
  };

  const handleBookNow = () => {
    navigate('/booking-type');
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SkeletonLoader />
        </div>
        <div className="dashboard-footer">
          <SFooterNav activeItem={activeNav} onItemClick={handleNavClick} />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <BannerCarousel banners={banners} />
        <div className="bookings-section">
          <div className="bookings-header">
            <b className="bookings-title">Bookings</b>
            <SButton
              variant="transparent"
              size="xs"
              label="View All"
              onClick={() => navigate('/bookings/all')}
              className="view-all-btn"
            />
          </div>

          <div className="bookings-content">
            {(() => {
              if (bookingsLoading) {
                return <SkeletonLoader />;
              }
              if (bookingsError) {
                return (
                  <div className="error-bookings">Failed to load bookings</div>
                );
              }
              if (filteredBookings && filteredBookings.length > 0) {
                return (
                  <div className="bookings-list">
                    <BookingCarousel
                      bookings={filteredBookings}
                      onCancel={handleCancelBooking}
                      onReschedule={handleBookingReschedule}
                      onCheckIn={handleCheckInBooking}
                      onVisitShop={handleVisitShop}
                      onOpenGate={handleOpenGate}
                      cancellingBookingId={cancellingBookingId}
                      isCheckingIn={isCheckingIn}
                      isRefreshing={isRefreshing}
                    />
                  </div>
                );
              }
              return (
                <div className="empty-bookings-card">
                  <div className="empty-bookings-card-content">
                    <div className="empty-bookings-icon">
                      <CalendarBlank size={22} weight="duotone" color="var(--tbx-gold)" />
                    </div>
                    <div className="empty-bookings-title">No Upcoming Sessions</div>
                    <div className="empty-bookings-description">
                      Book your session to access the facility and start your performance journey.
                    </div>
                  </div>
                  <button className="empty-bookings-cta" onClick={handleBookNow}>
                    Book a Session
                  </button>
                </div>
              );
            })()}
          </div>
        </div>

        <MeetYourPro />
        <TeebxMethod />
      </div>

      {/* {filteredBookings && filteredBookings.length > 0 && (
        <button className="book-now-fab" onClick={handleBookNow} aria-label="Book Session">
          <span className="book-now-fab__ping" />
          <img src={mainLogo} alt="Tee Box" className="book-now-fab__logo" />
          <span className="book-now-fab__label">Book</span>
        </button>
      )} */}

      <div className="dashboard-footer">
        <SFooterNav activeItem={activeNav} onItemClick={handleNavClick} />
      </div>

      {/* Cancel Booking Dialog */}
      <SDialog
        isOpen={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        title="Are you sure you want to cancel?"
        onConfirm={handleConfirmCancel}
        confirmText="Yes"
        cancelText="No"
      />

      {/* Shop Drawer */}
      <ShopDrawer
        isOpen={isShopDrawerOpen}
        onClose={handleShopDrawerClose}
        onContinue={handleShopContinue}
      />
    </div>
  );
};

export default Dashboard;
